import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import FabMenu from '~/components/FabMenu'
import Icon from '~/components/Icon'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import Select from '~/components/Select'
import SwipeAction from '~/components/SwipeAction'
import Switch from '~/components/Switch'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import { CATEGORY_PARAMETER_TYPE } from '~/layers/ProductForm'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { type Path, useNavigate } from '~/router'
import { parameterService } from '~/services/parameter.service'
import { encodeSpecFilter, PRODUCT_STATUS_OPTIONS, type ProductType, productService, toNumber, variantLabel } from '~/services/product.service'
import { resolveError } from '~/utils/error'

import { P } from '~/utils/permissions'

const KIND_FILTER = [
  { value: '', title: 'Todo el catálogo' },
  { value: 'product', title: 'Sólo productos' },
  { value: 'service', title: 'Sólo servicios' }
]

const STATUS_FILTER = [{ value: '', title: 'Cualquier estado' }, ...PRODUCT_STATUS_OPTIONS.map(o => ({ value: o.value, title: o.title }))]

const statusColor: Record<string, 'success' | 'warning' | 'default'> = {
  published: 'success',
  draft: 'warning',
  archived: 'default'
}

const statusLabel: Record<string, string> = {
  published: 'Publicado',
  draft: 'Borrador',
  archived: 'Archivado'
}

const money = (value: unknown) => `$${toNumber(value).toLocaleString('es-CO')}`

/** Cuántos filtros activos hay (para el botón de limpiar y el badge). */
const activeFilterCount = (f: { kind: string; status: string; category: string; search: string; onlyLowStock: boolean }): number =>
  [f.kind, f.status, f.category, f.search].filter(v => v !== '').length + (f.onlyLowStock ? 1 : 0)

/**
 * Catálogo de lo que vende la empresa: servicios y productos en la misma tabla,
 * porque para cotizar son lo mismo y separarlos obligaba a mirar en dos sitios.
 * Lo que los distingue —variantes y existencias— sólo aparece cuando lo hay.
 */
const CatalogoPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const isMobile = useIsMobile()

  const [filtersOpen, setFiltersOpen] = createSignal(false)

  // ── Filtros que viajan al backend ────────────────────────────────────────
  const [kind, setKind] = createSignal('')
  const [status, setStatus] = createSignal('')
  /**
   * Características, por clave normalizada: { color: ['rojo','azul'] }. Va al
   * backend y no se filtra aquí porque el listado sólo carga una página: filtrarlo
   * en cliente mentiría en cuanto el catálogo pasara de ese tamaño.
   */
  const [specFilters, setSpecFilters] = createSignal<Record<string, string[]>>({})
  /** Categoría (id como texto para el Select). También viaja al backend. */
  const [category, setCategory] = createSignal('')
  const [onlyLowStock, setOnlyLowStock] = createSignal(false)
  /** Búsqueda con debounce; el backend busca por nombre, SKU o clave. */
  const [search, debouncedSearch, setSearch] = useDebouncedSignal('')

  // Filtros compartidos entre la tabla (desktop) y el scroll infinito (móvil).
  const buildParams = (): Record<string, string | number | undefined> => {
    const p: Record<string, string | number | undefined> = {}
    const q = debouncedSearch().trim()
    if (q) p.q = `%${q}%`
    if (kind()) p.kind = kind()
    if (status()) p.status = status()
    if (category()) p.categoryId = Number(category())
    if (onlyLowStock()) p.lowStock = 1
    const specs = encodeSpecFilter(specFilters())
    if (specs) p.specs = specs
    return p
  }

  const table = useServerTable<ProductType>({
    fetcher: params => productService.list(params as Parameters<typeof productService.list>[0]),
    params: buildParams
  })

  // ── Scroll infinito móvil ────────────────────────────────────────────────
  // Se hace con un fetcher propio (y no reutilizando `table.onPageChange`) para
  // controlar el timing de forma determinista: `table.data()` en la transición
  // de página todavía trae la página anterior, lo que duplicaba o perdía filas.
  const [mobileItems, setMobileItems] = createStore<ProductType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await productService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // error silencioso: el sentinela reintentará en el siguiente scroll
    } finally {
      setMobileLoading(false)
    }
  }

  // Reinicia la lista (y recarga desde la página 1) cuando cambian los filtros.
  // Al entrar en móvil la clave pasa de '' a los filtros actuales, así que este
  // mismo efecto hace la primera carga sin duplicar peticiones.
  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([kind(), status(), category(), onlyLowStock(), specFilters(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })

  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  /** Categorías disponibles para filtrar, desde el parámetro de catálogo. */
  const [categories] = createResource(async () => {
    try {
      const result = await parameterService.list({ type: CATEGORY_PARAMETER_TYPE, limit: 200 })
      return result.items
    } catch {
      return []
    }
  })

  const categoryOptions = createMemo(() => [{ value: '', title: 'Cualquier categoría' }, ...(categories() ?? []).map(c => ({ value: String(c.id), title: c.label }))])

  /** Características disponibles para filtrar; se rehace al cambiar el tipo. */
  const [facets] = createResource(
    () => ({ kind: kind() }),
    async ({ kind: k }) => {
      try {
        return await productService.facets({ kind: (k || undefined) as ProductType['kind'] | undefined })
      } catch {
        return []
      }
    }
  )

  /** Característica elegida en el Select; sus valores son los que se pueden marcar. */
  const [facetKey, setFacetKey] = createSignal('')

  const facetOptions = createMemo(() => [{ value: '', title: 'Cualquier característica' }, ...(facets() ?? []).map(f => ({ value: f.key, title: `${f.label} · ${f.total}` }))])

  const facetValueOptions = createMemo(() => (facets() ?? []).find(f => f.key === facetKey())?.values ?? [])

  /** Marcar o desmarcar un valor: varios de la misma característica son "o". */
  const toggleFacetValue = (key: string, value: string) => {
    setSpecFilters(current => {
      const values = current[key] ?? []
      const next = values.includes(value) ? values.filter(v => v !== value) : [...values, value]
      const { [key]: _drop, ...rest } = current
      return next.length > 0 ? { ...rest, [key]: next } : rest
    })
  }

  /** Pares activos, ya con su etiqueta bonita, para pintarlos como chips. */
  const activeSpecChips = createMemo(() =>
    Object.entries(specFilters()).flatMap(([key, values]) => {
      const group = (facets() ?? []).find(f => f.key === key)
      return values.map(value => ({
        key,
        value,
        label: `${group?.label ?? key}: ${group?.values.find(v => v.value === value)?.label ?? value}`
      }))
    })
  )

  /**
   * Stats del listado. `total` viene del backend (`table.total()`) para que la
   * tarjeta refleje todo el catálogo con los filtros; el desglose (productos,
   * servicios, bajo mínimos) se calcula sobre la página actual de `table.data()`,
   * porque el endpoint no devuelve esos agregados.
   */
  const stats = createMemo(() => {
    const list = table.data()
    return {
      total: table.total(),
      productos: list.filter(p => p.kind === 'product').length,
      servicios: list.filter(p => p.kind === 'service').length,
      bajoMinimo: list.filter(p => p.lowStock).length
    }
  })

  const activeFilters = createMemo(
    () =>
      activeFilterCount({
        kind: kind(),
        status: status(),
        category: category(),
        search: search(),
        onlyLowStock: onlyLowStock()
      }) + activeSpecChips().length
  )

  const clearFilters = () => {
    setKind('')
    setStatus('')
    setCategory('')
    setSearch('')
    setOnlyLowStock(false)
    setSpecFilters({})
    setFacetKey('')
  }

  const handleDelete = async (product: ProductType) => {
    const ok = await alert.confirmDelete({
      title: `Eliminar ${product.kind === 'service' ? 'servicio' : 'producto'}`,
      message: `«${product.name}» dejará de estar disponible al cotizar. Si tiene movimientos de inventario se archivará en vez de borrarse.`
    })
    if (!ok) return

    try {
      await productService.delete(product.id)
      toast.addToast({ title: 'Elemento eliminado', type: 'success' })
      // En la vista móvil la lista se acumula en `mobileItems`: quitarla también
      // de ahí, o el `refetch` (misma página) no la actualizaría.
      setMobileItems(prev => prev.filter(i => i.id !== product.id))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar elementos',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'elemento' : 'elementos'}? Los que tengan movimientos de inventario se archivarán en vez de borrarse.`
    })
    if (!ok) return
    try {
      const res = await productService.bulkDelete(ids)
      const archivedNote = res.archived > 0 ? ` (${res.archived} archivados por tener movimientos)` : ''
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'elemento eliminado' : 'elementos eliminados'}${archivedNote}`, type: 'success' })
      setSelectedIds(new Set<number>())
      setMobileItems(prev => prev.filter(i => !ids.includes(i.id)))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const columns = [
    {
      header: 'Nombre',
      sticky: 'left' as const,
      minWidth: 260,
      accessor: (p: ProductType) => (
        <div class="flex items-center gap-3">
          {/* La miniatura va incrustada como data URI: no hay almacenamiento de ficheros. */}
          <div class="size-10 shrink-0 rounded-lg border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
            <Show
              when={p.images?.[0]}
              fallback={<Icon icon={p.kind === 'service' ? 'material-symbols-light:handyman-outline' : 'material-symbols-light:inventory-2-outline'} size={20} class="text-foreground-subtle" />}
            >
              <img src={p.images![0]} alt={p.name} class="max-h-full max-w-full object-contain" />
            </Show>
          </div>
          <div class="min-w-0">
            <div class="font-medium text-foreground truncate">{p.name}</div>
            <div class="text-xs text-foreground-subtle truncate">{p.sku || p.category?.label || p.key}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Tipo',
      accessor: (p: ProductType) => (
        <Badge color={p.kind === 'service' ? 'info' : 'primary'} variant="soft">
          {p.kind === 'service' ? 'Servicio' : 'Producto'}
        </Badge>
      )
    },
    { header: 'Categoría', accessor: (p: ProductType) => p.category?.label ?? '—' },
    {
      header: 'Precio',
      accessor: (p: ProductType) => (
        <div class="flex flex-col">
          <span class="text-foreground">{money(p.price)}</span>
          <Show when={p.compareAtPrice && toNumber(p.compareAtPrice) > toNumber(p.price)}>
            <span class="text-xs text-foreground-subtle line-through">{money(p.compareAtPrice)}</span>
          </Show>
        </div>
      )
    },
    {
      header: 'Variantes',
      accessor: (p: ProductType) => (
        <Show when={p.variants?.length} fallback={<span class="text-foreground-subtle">—</span>}>
          <span class="text-foreground" title={p.variants!.map(variantLabel).join(', ')}>
            {p.variants!.length}
          </span>
        </Show>
      )
    },
    {
      header: 'Existencias',
      accessor: (p: ProductType) => (
        <Show when={p.trackInventory} fallback={<span class="text-foreground-subtle">No se inventaría</span>}>
          <div class="flex items-center gap-2">
            <span class={p.lowStock ? 'text-danger font-medium' : 'text-foreground'}>{toNumber(p.stockTotal ?? p.stock).toLocaleString('es-CO')}</span>
            <Show when={p.lowStock}>
              <Icon icon="material-symbols-light:warning-outline" size={18} class="text-danger" />
            </Show>
          </div>
        </Show>
      )
    },
    {
      header: 'Estado',
      accessor: (p: ProductType) => (
        <Chip color={statusColor[p.status] ?? 'default'} variant="soft" size="xs">
          {statusLabel[p.status] ?? p.status}
        </Chip>
      )
    }
  ]

  return (
    <>
      <Title>Catálogo | Florez Jaramillo</Title>

      <div class="flex gap-4 overflow-x-auto hide-scrollbar mb-6 lg:grid lg:grid-cols-4 lg:overflow-visible">
        <Card class="min-w-40 lg:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-2xl font-bold text-foreground">{stats().total}</p>
                <p class="text-xs text-foreground-subtle">En el catálogo</p>
              </div>
              <Icon icon="material-symbols-light:inventory-2-outline" size={24} class="text-primary" />
            </div>
          </Card.Body>
        </Card>
        <Card class="min-w-40 lg:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-2xl font-bold text-foreground">{stats().productos}</p>
                <p class="text-xs text-foreground-subtle">Productos</p>
              </div>
              <Icon icon="material-symbols-light:package-2-outline" size={24} class="text-info" />
            </div>
          </Card.Body>
        </Card>
        <Card class="min-w-40 lg:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-2xl font-bold text-foreground">{stats().servicios}</p>
                <p class="text-xs text-foreground-subtle">Servicios</p>
              </div>
              <Icon icon="material-symbols-light:handyman-outline" size={24} class="text-success" />
            </div>
          </Card.Body>
        </Card>
        <Card class="min-w-40 lg:min-w-0">
          <Card.Body>
            <div class="flex items-center justify-between">
              <div>
                <p class={`text-2xl font-bold ${stats().bajoMinimo > 0 ? 'text-danger' : 'text-foreground'}`}>{stats().bajoMinimo}</p>
                <p class="text-xs text-foreground-subtle">Bajo mínimos</p>
              </div>
              <Icon icon="material-symbols-light:warning-outline" size={24} class={stats().bajoMinimo > 0 ? 'text-danger' : 'text-foreground-subtle'} />
            </div>
          </Card.Body>
        </Card>
      </div>

      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Show when={!isMobile()}>
        <Table<ProductType>
          title="Catálogo"
          subtitle={activeFilters() > 0 ? `${table.total()} elementos con los filtros actuales` : 'Lo que ofreces: servicios y productos que puedes añadir a una cotización'}
          headerActions={
            <div class="hidden sm:flex items-center gap-2">
              <FiltersButton activeCount={activeFilters()} onClick={() => setFiltersOpen(true)} />
              <Show when={can(P.products.create)}>
                <Button onClick={() => navigate('/admin/servicios/nuevo')} prependIcon="material-symbols-light:add-circle-outline">
                  Nuevo
                </Button>
              </Show>
            </div>
          }
          data={table.data()}
          columns={columns}
          keyField="id"
          headSticky
          loading={table.loading()}
          serverSide
          selectable={can(P.products.delete)}
          selectableColor="danger"
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={() => void handleBulkDelete()}
          total={table.total()}
          page={table.page()}
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          emptyMessage="No hay elementos que coincidan con los filtros"
          onRowClick={p => navigate(`/admin/servicios/${p.id}` as Path)}
          actions={product => (
            <ButtonGroup size="sm" variant="soft">
              <Show when={can(P.products.update)}>
                <Button
                  color="warning"
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/servicios/${product.id}` as Path)
                  }}
                  tooltip="Editar"
                  icon="material-symbols-light:edit-outline"
                />
              </Show>
              <Show when={product.trackInventory && can(P.inventory.read)}>
                <Button
                  color="info"
                  onClick={e => {
                    e.stopPropagation()
                    navigate(`/admin/servicios/${product.id}?tab=inventario` as Path)
                  }}
                  tooltip="Existencias"
                  icon="material-symbols-light:warehouse-outline"
                />
              </Show>
              <Show when={can(P.products.delete)}>
                <Button
                  color="danger"
                  onClick={e => {
                    e.stopPropagation()
                    handleDelete(product)
                  }}
                  tooltip="Eliminar"
                  icon="material-symbols-light:delete-outline"
                />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      {/* Vista móvil: en pantallas pequeñas la tabla desborda, así que se
          sustituye por tarjetas con acciones deslizables y scroll infinito. */}
      <Show when={isMobile()}>
        <Show when={can(P.products.delete) && selectedIds().size > 0}>
          <div class="mb-3 flex items-center justify-between gap-2 rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3">
            <span class="text-sm font-semibold text-danger">
              {selectedIds().size} {selectedIds().size === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline" onClick={() => void handleBulkDelete()}>
              Eliminar
            </Button>
          </div>
        </Show>
        <InfiniteList<ProductType>
          items={mobileItems}
          keyField={p => p.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={<p class="py-10 text-center text-foreground-subtle">No hay elementos que coincidan con los filtros</p>}
          endMessage={<span>Has llegado al final del catálogo</span>}
          class="space-y-3"
          renderItem={product => (
            <SwipeAction
              itemId={product.id}
              rememberPosition
              onClick={() => navigate(`/admin/servicios/${product.id}` as Path)}
              leftOptions={[
                ...(can(P.products.update)
                  ? [
                      {
                        icon: 'material-symbols-light:edit-outline',
                        label: 'Editar',
                        color: 'warning',
                        action: () => navigate(`/admin/servicios/${product.id}` as Path)
                      }
                    ]
                  : []),
                ...(product.trackInventory && can(P.inventory.read)
                  ? [
                      {
                        icon: 'material-symbols-light:warehouse-outline',
                        label: 'Existencias',
                        color: 'info',
                        action: () => navigate(`/admin/servicios/${product.id}?tab=inventario` as Path)
                      }
                    ]
                  : [])
              ]}
              rightOptions={[
                ...(can(P.products.delete)
                  ? [
                      {
                        icon: 'material-symbols-light:delete-outline',
                        label: 'Eliminar',
                        color: 'danger',
                        action: () => handleDelete(product)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body class="flex items-center gap-3">
                  <Show when={can(P.products.delete)}>
                    <Checkbox size="sm" color="danger" checked={selectedIds().has(product.id)} onChange={() => toggleSelect(product.id)} class="shrink-0" />
                  </Show>
                  <div class="size-11 shrink-0 rounded-lg border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
                    <Show
                      when={product.images?.[0]}
                      fallback={
                        <Icon icon={product.kind === 'service' ? 'material-symbols-light:handyman-outline' : 'material-symbols-light:inventory-2-outline'} size={22} class="text-foreground-subtle" />
                      }
                    >
                      <img src={product.images![0]} alt={product.name} class="max-h-full max-w-full object-contain" />
                    </Show>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-between gap-2">
                      <p class="font-medium text-foreground truncate">{product.name}</p>
                      <Badge color={product.kind === 'service' ? 'info' : 'primary'} variant="soft" class="shrink-0">
                        {product.kind === 'service' ? 'Servicio' : 'Producto'}
                      </Badge>
                    </div>
                    <p class="text-xs text-foreground-subtle truncate">{product.sku || product.category?.label || product.key}</p>
                    <div class="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span class="text-sm font-semibold text-foreground">{money(product.price)}</span>
                      <Show when={product.trackInventory}>
                        <span class={`text-xs ${product.lowStock ? 'text-danger font-medium' : 'text-foreground-subtle'}`}>
                          {toNumber(product.stockTotal ?? product.stock).toLocaleString('es-CO')} en stock
                        </span>
                      </Show>
                      <Chip color={statusColor[product.status] ?? 'default'} variant="soft" size="xs">
                        {statusLabel[product.status] ?? product.status}
                      </Chip>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <TableFilters
        title="Filtros del catálogo"
        isOpen={filtersOpen()}
        onClose={() => setFiltersOpen(false)}
        activeCount={activeFilters()}
        onClear={clearFilters}
        chips={[
          ...(search() ? [{ label: `«${search()}»`, color: 'info' as const, icon: 'material-symbols-light:search', onRemove: () => setSearch('') }] : []),
          ...(kind() ? [{ label: KIND_FILTER.find(o => o.value === kind())?.title ?? kind(), color: 'primary' as const, onRemove: () => setKind('') }] : []),
          ...(status() ? [{ label: statusLabel[status()] ?? status(), color: 'warning' as const, onRemove: () => setStatus('') }] : []),
          ...(category() ? [{ label: categoryOptions().find(o => o.value === category())?.title ?? 'Categoría', color: 'success' as const, onRemove: () => setCategory('') }] : []),
          ...(onlyLowStock() ? [{ label: 'Bajo mínimo', color: 'danger' as const, onRemove: () => setOnlyLowStock(false) }] : []),
          ...activeSpecChips().map(chip => ({
            label: chip.label,
            color: 'secondary' as const,
            icon: 'material-symbols-light:list-alt-outline',
            onRemove: () => toggleFacetValue(chip.key, chip.value)
          }))
        ]}
      >
        <Input label="Buscar" value={search()} onInput={e => setSearch(e.currentTarget.value)} placeholder="Nombre, SKU o clave..." startIcon="material-symbols-light:search" />

        <Select label="Tipo" options={KIND_FILTER} value={kind()} onChange={v => setKind(String(v ?? ''))} class="w-full" />

        <Select label="Estado" options={STATUS_FILTER} value={status()} onChange={v => setStatus(String(v ?? ''))} class="w-full" />

        <Select label="Categoría" options={categoryOptions()} value={category()} onChange={v => setCategory(String(v ?? ''))} class="w-full" />

        {/* Características: sale de la ficha técnica y de los ejes de variante. */}
        <Show when={(facets() ?? []).length > 0}>
          <div class="border-t border-outline/30 pt-4">
            <p class="text-sm font-medium text-foreground mb-3">Características</p>
            <Select label="Característica" options={facetOptions()} value={facetKey()} onChange={v => setFacetKey(String(v ?? ''))} class="w-full" />

            <Show when={facetValueOptions().length > 0}>
              <div class="flex flex-wrap gap-1.5 mt-3">
                <For each={facetValueOptions()}>
                  {value => (
                    <Chip
                      size="sm"
                      variant={(specFilters()[facetKey()] ?? []).includes(value.value) ? 'filled' : 'soft'}
                      color={(specFilters()[facetKey()] ?? []).includes(value.value) ? 'primary' : 'secondary'}
                      class="cursor-pointer"
                      onClick={() => toggleFacetValue(facetKey(), value.value)}
                    >
                      {value.label} · {value.total}
                    </Chip>
                  )}
                </For>
              </div>
              <p class="text-xs text-foreground-subtle mt-2">Varios valores de la misma característica se suman; características distintas se cruzan.</p>
            </Show>
          </div>
        </Show>

        <div class="border-t border-outline/30 pt-4">
          <p class="text-sm font-medium text-foreground mb-3">Inventario</p>
          <div class="flex flex-col gap-3">
            <Switch label="Sólo bajo mínimo" checked={onlyLowStock()} onChange={e => setOnlyLowStock(e.currentTarget.checked)} />
          </div>
        </div>
      </TableFilters>

      {/* FAB móvil: mismas acciones del header, accesibles en pantallas pequeñas */}
      <div class="sm:hidden">
        <FabMenu
          items={[
            { label: 'Filtros', icon: 'material-symbols-light:tune', onClick: () => setFiltersOpen(true) },
            ...(can(P.products.create) ? [{ label: 'Nuevo', icon: 'material-symbols-light:add-circle-outline', onClick: () => navigate('/admin/servicios/nuevo' as Path) }] : [])
          ]}
        />
      </div>

      <alert.AlertComponent />
    </>
  )
}

export default CatalogoPage
